import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useSlotGroup } from '@vibesites/runtime/react';
import { contactLinkDef, investorDef, portalDef, socialDef } from './Header';

const MENU_BACKGROUND_URL =
  'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0f3b3-4638-77ec-931c-faa1b0b98304.webp';

const MENU_SECTIONS = [
  {
    id: 'live',
    label: 'Live With Us',
    links: [
      { href: '/our-communities', label: 'Our Communities' },
      { href: '/landmark360', label: 'Landmark360®' },
      { href: '/resident-portal', label: 'Resident Portal' },
    ],
  },
  {
    id: 'invest',
    label: 'Invest With Us',
    links: [
      { href: '/investors', label: 'Investors' },
      { href: '/ala-insights', label: 'ALA Insights' },
      { href: '/news', label: 'News' },
    ],
  },
  {
    id: 'careers',
    label: 'Explore Careers',
    links: [
      { href: '/careers', label: 'Careers' },
      { href: '/culture', label: 'Our Culture' },
      { href: '/our-impact', label: 'Corporate Responsibility' },
    ],
  },
  {
    id: 'about',
    label: 'About ALA',
    links: [
      { href: '/about-us', label: 'About Us' },
      { href: '/technology', label: 'Technology' },
      { href: '/team', label: 'Leadership' },
      { href: '/faqs', label: 'FAQs' },
    ],
  },
] as const;

interface MenuLink {
  href: string;
  label: string;
  external: boolean;
}

function useMenuFooterLinks() {
  const social = useSlotGroup('header.social', socialDef);
  const investor = useSlotGroup('header.investor_login', investorDef);
  const portal = useSlotGroup('header.resident_portal', portalDef);
  const contact = useSlotGroup('header.contact_link', contactLinkDef);

  const connect: MenuLink[] = [
    { href: social.instagram, label: 'Instagram', external: true },
    { href: social.linkedin, label: 'LinkedIn', external: true },
    { href: social.facebook, label: 'Facebook', external: true },
  ].filter((link): link is MenuLink => Boolean(link.href));

  const account: MenuLink[] = [
    {
      href: portal.url || '/resident-portal',
      label: portal.label || 'Resident Portal',
      external: portal._target === '_blank',
    },
    {
      href: investor.url || 'https://www.alapts.com/investors',
      label: investor.label || 'Investor Login',
      external: (investor._target || '_blank') === '_blank',
    },
    {
      href: contact.url || '/contact-us',
      label: contact.label || 'Contact Us',
      external: contact._target === '_blank',
    },
  ];

  return { connect, account };
}

interface SiteMenuProps {
  logoUrl: string;
  logoAlt: string;
  backgroundUrl?: string | null;
  trigger?: ReactNode;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ala-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function normalizePath(path: string) {
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Starts a freshly navigated page at the top. Back/forward, reloads and
 * `#anchor` URLs keep the browser's own scroll handling.
 */
export function useScrollToTopOnPageLoad() {
  useEffect(() => {
    if (window.location.hash) return;
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav && (nav.type === 'back_forward' || nav.type === 'reload')) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const frame = requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }));
    return () => cancelAnimationFrame(frame);
  }, []);
}

function PlusIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-5 w-5 transition-transform duration-300 ease-out md:h-6 md:w-6 ${rotated ? 'rotate-45' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 opacity-60" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M7 17L17 7M9 7h8v8" />
    </svg>
  );
}

function CloseIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function SiteMenu({ logoUrl, logoAlt, backgroundUrl, trigger }: SiteMenuProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const panelId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const { connect: connectLinks, account: accountLinks } = useMenuFooterLinks();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const path = normalizePath(window.location.pathname);
    setCurrentPath(path);
    const activeSection = MENU_SECTIONS.find((section) =>
      section.links.some((link) => normalizePath(link.href) === path),
    );
    setExpandedId(activeSection?.id ?? null);
  }, [open]);

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      const frame = requestAnimationFrame(() => closeButtonRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }
    returnFocusRef.current?.focus?.();
    returnFocusRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const closeMenu = () => setOpen(false);

  const navigateFromMenu = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    closeMenu();
  };

  const toggleSection = (id: string) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  const defaultTrigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`inline-flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--ala-on-primary))]/10 text-[rgb(var(--ala-on-primary))] transition hover:bg-[rgb(var(--ala-on-primary))]/20 lg:h-[60px] lg:w-[60px] ${FOCUS_RING}`}
      aria-label="Open menu"
      aria-expanded={open}
      aria-controls={panelId}
    >
      <span className="flex flex-col gap-1.5" aria-hidden>
        <span className="block h-0.5 w-5 bg-current" />
        <span className="block h-0.5 w-5 bg-current" />
        <span className="block h-0.5 w-5 bg-current" />
      </span>
    </button>
  );

  return (
    <>
      {trigger ? (
        <span className="inline-flex" onClick={() => setOpen(true)}>
          {trigger}
        </span>
      ) : (
        defaultTrigger
      )}

      {mounted
        ? createPortal(
            <MotionConfig reducedMotion="user">
            <AnimatePresence>
              {open ? (
                <motion.div
                  ref={dialogRef}
                  id={panelId}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Site menu"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="fixed inset-0 z-[100] flex flex-col overscroll-contain text-[rgb(var(--ala-on-primary))]"
                >
                  <div className="absolute inset-0">
                    <VibesitesImage
                      url={backgroundUrl || MENU_BACKGROUND_URL}
                      alt=""
                      sizes="100vw"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[rgb(var(--ala-dark))]/[0.82]" />
                  </div>

                  <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                    <header className="flex shrink-0 items-center justify-between px-6 py-6 pt-[max(1.5rem,env(safe-area-inset-top))] md:px-10 lg:px-14">
                      <a href="/" onClick={navigateFromMenu} className={`shrink-0 rounded-sm ${FOCUS_RING}`} aria-label={`${logoAlt} home`}>
                        <VibesitesImage
                          url={logoUrl}
                          alt={logoAlt}
                          width={180}
                          height={48}
                          objectFit="contain"
                          sizes="180px"
                          className="h-10 w-auto md:h-12"
                        />
                      </a>
                      <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={closeMenu}
                        className={`inline-flex min-h-11 min-w-11 items-center justify-center gap-2.5 rounded-full border border-[rgb(var(--ala-on-primary))]/25 px-4 font-heading text-xs font-medium uppercase tracking-[0.18em] text-[rgb(var(--ala-on-primary))] transition hover:border-[rgb(var(--ala-on-primary))]/60 hover:bg-[rgb(var(--ala-on-primary))]/10 md:text-sm ${FOCUS_RING}`}
                        aria-label="Close menu"
                      >
                        <span>Close</span>
                        <CloseIcon className="h-4 w-4 md:h-5 md:w-5" />
                      </button>
                    </header>

                    <div className="mx-6 h-px shrink-0 bg-[rgb(var(--ala-on-primary))]/20 md:mx-10 lg:mx-14" />

                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                    <motion.nav
                      className="flex shrink-0 flex-col px-6 md:px-10 lg:px-14"
                      initial="hidden"
                      animate="visible"
                      variants={{
                        hidden: {},
                        visible: {
                          transition: { staggerChildren: 0.1, delayChildren: 0.18 },
                        },
                      }}
                    >
                      <ul className="flex flex-col">
                        {MENU_SECTIONS.map((section) => {
                          const isExpanded = expandedId === section.id;
                          const isDimmed = expandedId !== null && !isExpanded;
                          const sectionPanelId = `${panelId}-${section.id}`;
                          return (
                            <motion.li
                              key={section.id}
                              className="border-b border-[rgb(var(--ala-on-primary))]/20"
                              variants={{
                                hidden: { opacity: 0, y: 18 },
                                visible: {
                                  opacity: 1,
                                  y: 0,
                                  transition: {
                                    duration: 0.7,
                                    ease: [0.22, 1, 0.36, 1],
                                  },
                                },
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => toggleSection(section.id)}
                                aria-expanded={isExpanded}
                                aria-controls={sectionPanelId}
                                className={`group flex w-full items-center justify-between gap-6 rounded-sm py-4 text-left transition-opacity duration-300 md:py-5 ${
                                  isDimmed ? 'opacity-50 hover:opacity-100 focus-visible:opacity-100' : 'opacity-100'
                                } ${FOCUS_RING}`}
                              >
                                <span className="font-heading text-[clamp(1.75rem,7vw,60px)] font-medium uppercase leading-[1.05] tracking-[-0.03em] transition-transform duration-300 ease-out group-hover:translate-x-1.5">
                                  {section.label}
                                </span>
                                <span
                                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 md:h-12 md:w-12 ${
                                    isExpanded
                                      ? 'border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-accent))] text-[rgb(var(--ala-dark))]'
                                      : 'border-[rgb(var(--ala-on-primary))]/30 text-[rgb(var(--ala-on-primary))] group-hover:border-[rgb(var(--ala-on-primary))]/70'
                                  }`}
                                  aria-hidden
                                >
                                  <PlusIcon rotated={isExpanded} />
                                </span>
                              </button>

                              <AnimatePresence initial={false}>
                                {isExpanded ? (
                                  <motion.div
                                    id={sectionPanelId}
                                    key={section.id}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                                    className="overflow-hidden"
                                  >
                                    <motion.ul
                                      className="flex flex-col gap-0.5 pb-6 pl-0.5 md:flex-row md:flex-wrap md:gap-x-8 md:pb-8"
                                      initial="hidden"
                                      animate="visible"
                                      variants={{
                                        hidden: {},
                                        visible: {
                                          transition: {
                                            staggerChildren: 0.06,
                                            delayChildren: 0.08,
                                          },
                                        },
                                      }}
                                    >
                                      {section.links.map((link) => {
                                        const isCurrent = normalizePath(link.href) === currentPath;
                                        return (
                                        <motion.li
                                          key={link.href}
                                          variants={{
                                            hidden: { opacity: 0, y: 8 },
                                            visible: {
                                              opacity: 1,
                                              y: 0,
                                              transition: {
                                                duration: 0.45,
                                                ease: [0.22, 1, 0.36, 1],
                                              },
                                            },
                                          }}
                                        >
                                          <a
                                            href={link.href}
                                            onClick={navigateFromMenu}
                                            aria-current={isCurrent ? 'page' : undefined}
                                            className={`group/link inline-flex min-h-11 items-center gap-3 rounded-sm font-heading text-lg transition md:text-xl ${
                                              isCurrent
                                                ? 'text-[rgb(var(--ala-accent))]'
                                                : 'text-[rgb(var(--ala-on-primary))]/90 hover:text-[rgb(var(--ala-accent))]'
                                            } ${FOCUS_RING}`}
                                          >
                                            <span
                                              className={`h-px bg-current transition-all duration-300 ${
                                                isCurrent ? 'w-6' : 'w-3 group-hover/link:w-6'
                                              }`}
                                              aria-hidden
                                            />
                                            {link.label}
                                          </a>
                                        </motion.li>
                                        );
                                      })}
                                    </motion.ul>
                                  </motion.div>
                                ) : null}
                              </AnimatePresence>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </motion.nav>

                    <motion.footer
                      className="flex shrink-0 flex-col gap-6 px-6 pt-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:flex-row md:items-start md:justify-between md:gap-12 md:px-10 md:py-8 lg:px-14"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.65, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div>
                        <p className="font-heading text-[11px] font-medium uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/45">
                          Connect
                        </p>
                        <ul className="mt-1 flex flex-wrap items-center gap-x-8">
                          {connectLinks.map((link) => (
                            <li key={link.href}>
                              <a
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex min-h-11 items-center gap-1.5 rounded-sm font-heading text-sm text-[rgb(var(--ala-on-primary))] transition hover:text-[rgb(var(--ala-accent))] md:text-base ${FOCUS_RING}`}
                              >
                                {link.label}
                                <ExternalIcon />
                                <span className="sr-only">(opens in a new tab)</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="md:text-right">
                        <p className="font-heading text-[11px] font-medium uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/45">
                          Account
                        </p>
                        <ul className="mt-1 flex flex-wrap items-center gap-x-8 md:justify-end">
                          {accountLinks.map((link) => {
                            const isExternal = link.external;
                            const isCurrent = !isExternal && normalizePath(link.href) === currentPath;
                            return (
                            <li key={link.href}>
                              <a
                                href={link.href}
                                onClick={isExternal ? closeMenu : navigateFromMenu}
                                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                aria-current={isCurrent ? 'page' : undefined}
                                className={`inline-flex min-h-11 items-center gap-1.5 rounded-sm font-heading text-sm transition hover:text-[rgb(var(--ala-accent))] md:text-base ${
                                  isCurrent ? 'text-[rgb(var(--ala-accent))]' : 'text-[rgb(var(--ala-on-primary))]'
                                } ${FOCUS_RING}`}
                              >
                                {link.label}
                                {isExternal ? (
                                  <>
                                    <ExternalIcon />
                                    <span className="sr-only">(opens in a new tab)</span>
                                  </>
                                ) : null}
                              </a>
                            </li>
                            );
                          })}
                        </ul>
                      </div>
                    </motion.footer>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
            </MotionConfig>,
            document.body,
          )
        : null}
    </>
  );
}

export default SiteMenu;
